import React, { useMemo, useState } from 'react'
import { useController } from 'react-hook-form'
import { useTranslate, Button } from 'react-admin'
import debounce from 'lodash.debounce'

import {
    Box,
    Button as MuiButton,
    CircularProgress,
    Typography,
    useMediaQuery,
    useTheme,
    TextField,
    Autocomplete,
} from '@mui/material'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import { autocompleteInputCommonStyle } from '../../themes/commonStyles'
import { reverseGeocode } from '../../rest/restClient'
import { getMarkerDefaultIcon } from '../../ui/maps/icons'
import StandardDialog from '../../ui/dialog/StandardDialog'


const DEFAULT_CENTER = { lat: 48.8738, lng: 2.2950 }
const DEFAULT_ZOOM = 16


const fetchGeoapifySuggestions = async (query) => {
    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY
    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=5&apiKey=${apiKey}`

    const response = await fetch(url)
    const data = await response.json()

    return data.features.map((feature) => ({
        label: feature.properties.formatted,
        lat: feature.geometry.coordinates[1],
        lon: feature.geometry.coordinates[0],
        country: feature.properties.country || '',
        city: feature.properties.city || feature.properties.town || feature.properties.village || '',
        postcode: feature.properties.postcode || '',
    }))
}


// Headless child: renders the draggable marker and lets the user click the map to move it.
const DraggableMarker = ({ position, onMove, icon }) => {
    useMapEvents({
        click(e) {
            onMove(e.latlng.lat, e.latlng.lng)
        },
    })

    if (!position || position.lat == null || position.lng == null) return null

    return (
        <Marker
            draggable
            icon={icon}
            position={[position.lat, position.lng]}
            eventHandlers={{
                dragend(e) {
                    const latlng = e.target.getLatLng()
                    onMove(latlng.lat, latlng.lng)
                },
            }}
        />
    )
}


const BodyComponent = ({ setOpenDialog, latitudeSource, longitudeSource, addressSource, countrySource, citySource, postcodeSource }) => {
    const translate = useTranslate()
    const theme = useTheme()
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'))
    const [loading, setLoading] = useState(false)

    const { field: latitudeField } = useController({ name: latitudeSource })
    const { field: longitudeField } = useController({ name: longitudeSource })
    const { field: formattedAddressField } = useController({ name: addressSource })
    const { field: countryField } = useController({ name: countrySource })
    const { field: cityField } = useController({ name: citySource })
    const { field: postcodeField } = useController({ name: postcodeSource })

    const initialLat = Number.isFinite(Number(latitudeField.value)) && latitudeField.value !== '' && latitudeField.value !== null
        ? Number(latitudeField.value) : DEFAULT_CENTER.lat
    const initialLng = Number.isFinite(Number(longitudeField.value)) && longitudeField.value !== '' && longitudeField.value !== null
        ? Number(longitudeField.value) : DEFAULT_CENTER.lng

    // `position` = the marker / source of truth for coordinates.
    const [position, setPosition] = useState({ lat: initialLat, lng: initialLng })
    // `mapCenter` + `zoom` drive the MapContainer; only changed on address search so
    // dragging the marker never makes the map jump/remount.
    const [mapCenter, setMapCenter] = useState({ lat: initialLat, lng: initialLng })
    const [zoom, setZoom] = useState(DEFAULT_ZOOM)
    const [place, setPlace] = useState(formattedAddressField.value || '')
    const [places, setPlaces] = useState([])
    // Address components (country / city / postcode) resolved from the picked point.
    // Seeded from the current record so confirming without moving keeps existing values.
    const [details, setDetails] = useState({
        country: countryField.value || '',
        city: cityField.value || '',
        postcode: postcodeField.value || '',
    })

    const markerIcon = useMemo(
        () => getMarkerDefaultIcon('addressIconUrl_24x24', theme.palette.primary.main),
        [theme]
    )

    const debouncedFetch = useMemo(() =>
        debounce(async (query) => {
            setLoading(true)
            try {
                const response = await fetchGeoapifySuggestions(query)
                setPlaces(response)
            } catch (error) {
                console.error('Error fetching places:', error)
            } finally {
                setLoading(false)
            }
        }, 800)
    , [])

    const handleInputChange = (event, newInputValue) => {
        if (event === null) return
        setPlace(newInputValue)
        if (typeof newInputValue !== 'string') return
        if (newInputValue.length > 4) {
            debouncedFetch(newInputValue)
        } else {
            setPlaces([])
        }
    }

    const onPlaceSelected = (_event, suggestion) => {
        if (suggestion && suggestion.lat && suggestion.lon) {
            setPosition({ lat: suggestion.lat, lng: suggestion.lon })
            setMapCenter({ lat: suggestion.lat, lng: suggestion.lon })
            setZoom(17)
            setPlace(suggestion.label)
            setDetails({
                country: suggestion.country || '',
                city: suggestion.city || '',
                postcode: suggestion.postcode || '',
            })
        }
    }

    const reverseGeocoding = (latitude, longitude) => {
        try {
            reverseGeocode(latitude, longitude)
                .then((response) => response.json)
                .then((data) => {
                    if (data && data.formatted_address) {
                        setPlace(data.formatted_address)
                        setPlaces([{ label: data.formatted_address, lat: latitude, lon: longitude }])
                        setDetails({
                            country: data.country || '',
                            city: data.city || data.town || data.village || '',
                            postcode: data.postcode || '',
                        })
                    }
                })
        } catch (ex) {
            // ignore reverse geocoding failures; coordinates are still valid
        }
    }

    // Called on marker drag end and on map click.
    const handleMarkerMove = (lat, lng) => {
        setPosition({ lat, lng })
        reverseGeocoding(lat, lng)
    }

    const handleConfirm = () => {
        if (position && position.lat != null) latitudeField.onChange(position.lat)
        if (position && position.lng != null) longitudeField.onChange(position.lng)
        if (place) formattedAddressField.onChange(place)
        countryField.onChange(details.country || '')
        cityField.onChange(details.city || '')
        postcodeField.onChange(details.postcode || '')
        setOpenDialog(false)
    }

    const mapWidth = isSmall ? 250 : 380

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: `${mapWidth}px`,
            maxWidth: '100%',
        }}>
            <Typography sx={{
                fontFamily: 'Roboto',
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18px',
                textAlign: 'center',
                color: theme.palette.text.secondary,
                margin: '0px 0px 12px 0px',
            }}>
                {translate('resources.mac_addresses.pick_location_help')}
            </Typography>

            <Autocomplete
                options={places}
                loading={loading}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.label === value.label}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
                onChange={onPlaceSelected}
                inputValue={place}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={translate('resources.mac_addresses.search_address')}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                sx={autocompleteInputCommonStyle({ theme, isSmall, width: `${mapWidth}px` })}
            />

            <Box sx={{ marginTop: '12px', width: `${mapWidth}px` }}>
                <MapContainer
                    key={`${mapCenter.lat},${mapCenter.lng},${zoom}`}
                    center={[mapCenter.lat, mapCenter.lng]}
                    zoom={zoom}
                    style={{ width: `${mapWidth}px`, height: '320px', borderRadius: '12px' }}
                >
                    <TileLayer
                        attribution="&amp;copy <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        crossOrigin="anonymous" 
                    />
                    <DraggableMarker position={position} onMove={handleMarkerMove} icon={markerIcon} />
                </MapContainer>
            </Box>

            <Typography sx={{
                fontFamily: 'Roboto',
                fontSize: '13px',
                fontWeight: 500,
                textAlign: 'center',
                color: theme.palette.text.primary,
                margin: '12px 0px 0px 0px',
            }}>
                {`${Number(position.lat).toFixed(6)}, ${Number(position.lng).toFixed(6)}`}
            </Typography>
            {place &&
                <Typography sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    textAlign: 'center',
                    color: theme.palette.text.secondary,
                    margin: '4px 0px 0px 0px',
                }}>
                    {place}
                </Typography>
            }

            <Button
                onClick={handleConfirm}
                sx={{
                    margin: '20px 0px 0px 0px',
                    padding: '8px', fontSize: '14px', borderRadius: '5px',
                    color: theme.palette.background.paper,
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                        color: theme.palette.background.paper,
                        backgroundColor: theme.palette.primary.main,
                    },
                }}>
                {translate('ra.action.confirm')}
            </Button>
        </Box>
    )
}


const LocationPickerInput = ({
    openDialog,
    setOpenDialog,
    latitudeSource = 'address.latitude',
    longitudeSource = 'address.longitude',
    addressSource = 'address.formatted_address',
    countrySource = 'address.country',
    citySource = 'address.city',
    postcodeSource = 'address.postcode',
}) => {
    return (
        <StandardDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            fullWidth={false}
            BodyComponent={() => (
                <BodyComponent
                    setOpenDialog={setOpenDialog}
                    latitudeSource={latitudeSource}
                    longitudeSource={longitudeSource}
                    addressSource={addressSource}
                    countrySource={countrySource}
                    citySource={citySource}
                    postcodeSource={postcodeSource}
                />
            )}
        />
    )
}


// Trigger button + dialog, ready to drop into a SimpleForm.
export const PickLocationButton = ({
    latitudeSource = 'address.latitude',
    longitudeSource = 'address.longitude',
    addressSource = 'address.formatted_address',
    countrySource = 'address.country',
    citySource = 'address.city',
    postcodeSource = 'address.postcode',
}) => {
    const theme = useTheme()
    const translate = useTranslate()
    const [openDialog, setOpenDialog] = useState(false)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0px 0px 16px 0px' }}>
            <MuiButton
                variant="outlined"
                startIcon={<PlaceOutlinedIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                }}
            >
                {translate('resources.mac_addresses.pick_location')}
            </MuiButton>
            <LocationPickerInput
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                latitudeSource={latitudeSource}
                longitudeSource={longitudeSource}
                addressSource={addressSource}
                countrySource={countrySource}
                citySource={citySource}
                postcodeSource={postcodeSource}
            />
        </Box>
    )
}


export default LocationPickerInput
